import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, Users, UserCheck, UserX, AlertCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { 
  updateMissingOneSignalIds, 
  getOneSignalIdStats 
} from '@/utils/onesignal-migration';

const OneSignalMigrationPanel: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [stats, setStats] = useState({
    total: 0,
    withIds: 0,
    withoutIds: 0,
    coverage: 0
  });
  const [usersWithoutIds, setUsersWithoutIds] = useState<Array<{id: string, email?: string, name?: string}>>([]);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load stats on component mount
  useEffect(() => {
    loadStats();
    
    // Set up periodic refresh every 2 minutes for OneSignal stats
    intervalRef.current = setInterval(() => {
      loadStats(true);
    }, 120000);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setStatsLoading(true);
    }
    
    try {
      const statsData = await getOneSignalIdStats();
      setStats(statsData);
      setLastUpdated(new Date());
    } catch (error) {
      
      toast({
        title: "İstatistik yüklenemedi",
        description: "OneSignal istatistikleri yüklenirken bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setStatsLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    loadStats(true);
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleMigration = async () => {
    setLoading(true);
    try {
      const result = await updateMissingOneSignalIds();
      
      if (result.success) {
        toast({
          title: "OneSignal Kontrolü Tamamlandı",
          description: result.message,
          variant: "default"
        });
        
        // Update the list of users without IDs
        setUsersWithoutIds(result.usersWithoutIds);
        
        // Reload stats
        await loadStats();
      } else {
        toast({
          title: "Kontrol hatası",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      
      toast({
        title: "Beklenmeyen hata",
        description: "OneSignal kontrolü sırasında bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getCoverageColor = (coverage: number) => {
    if (coverage >= 90) return 'text-green-600';
    if (coverage >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCoverageVariant = (coverage: number) => {
    if (coverage >= 90) return 'default';
    if (coverage >= 70) return 'secondary';
    return 'destructive';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          OneSignal Kullanıcı Migrasyonu
        </CardTitle>
        <CardDescription>
          Kullanıcıların OneSignal ID'lerini yönetin ve eksik olanları güncelleyin.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Statistics Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">İstatistikler</h3>
              <p className="text-sm text-muted-foreground">
                Son güncelleme: {formatLastUpdated()}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              disabled={statsLoading || refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {refreshing ? 'Yeniliyor...' : 'Yenile'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-4 border rounded-lg">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Toplam Kullanıcı</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 border rounded-lg">
              <UserCheck className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">OneSignal ID'si Olan</p>
                <p className="text-2xl font-bold">{stats.withIds}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 border rounded-lg">
              <UserX className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">OneSignal ID'si Olmayan</p>
                <p className="text-2xl font-bold">{stats.withoutIds}</p>
              </div>
            </div>
          </div>

          {/* Coverage Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Kapsama Oranı</span>
              <Badge variant={getCoverageVariant(stats.coverage)}>
                {stats.coverage}%
              </Badge>
            </div>
            <Progress value={stats.coverage} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {stats.total > 0 && stats.coverage < 100 && (
                <>
                  {stats.withoutIds} kullanıcının OneSignal ID'si eksik. 
                  Push bildirimleri gönderilemeyebilir.
                </>
              )}
              {stats.coverage === 100 && "Tüm kullanıcıların OneSignal ID'si mevcut!"}
            </p>
          </div>
        </div>

        {/* Migration Section */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-lg font-semibold">OneSignal ID Yönetimi</h3>
          
          {stats.withoutIds > 0 && (
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  OneSignal ID'si Eksik Kullanıcılar Bulundu
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {stats.withoutIds} kullanıcının OneSignal ID'si eksik. 
                  Bu kullanıcılara push bildirimleri gönderilemez.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <h4 className="font-medium mb-2">Tüm Kullanıcıları Kontrol Et</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Tüm kullanıcıları tarar ve OneSignal ID'si eksik olanları tespit eder. 
                Mevcut cihaz için OneSignal ID'si varsa günceller.
              </p>
              <Button
                onClick={handleMigration}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Kontrol ediliyor...
                  </>
                ) : (
                  <>
                    <UserCheck className="h-4 w-4 mr-2" />
                    Tüm Kullanıcıları Kontrol Et
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Users Without OneSignal IDs List */}
          {usersWithoutIds.length > 0 && (
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium">OneSignal ID'si Eksik Kullanıcılar ({usersWithoutIds.length})</h4>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {usersWithoutIds.map((user, index) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {user.name || user.email || `Kullanıcı ${index + 1}`}
                      </p>
                      {user.email && user.name && (
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      )}
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      ID Yok
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-blue-800 mb-1">💡 Otomatik Çözüm Aktif:</p>
                <p className="text-blue-700">
                  Bu kullanıcılar uygulamaya bir sonraki giriş yaptıklarında, 
                  OneSignal ID'leri otomatik olarak atanacak ve veritabanına kaydedilecektir.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="space-y-3 pt-4 border-t">
          <h3 className="text-lg font-semibold">Önemli Bilgiler</h3>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              <strong>OneSignal ID'si nedir?</strong> Her kullanıcının cihazına özgü benzersiz bir tanımlayıcıdır. 
              Push bildirimleri göndermek için gereklidir.
            </p>
            <p>
              <strong>Neden eksik olabilir?</strong> Kullanıcı henüz bildirim izni vermemiş, 
              uygulama güncellemesi sonrası ID kaybedilmiş veya OneSignal başlatılmamış olabilir.
            </p>
            <p>
              <strong>Teknik Sınırlama:</strong> OneSignal ID'leri cihaza özgüdür ve sadece 
              kullanıcının kendi cihazında oluşturulabilir. Admin panelinden diğer kullanıcılara 
              ID atanamaz.
            </p>
            <p>
              <strong>Otomatik Çözüm:</strong> Sistem, eksik ID'ye sahip kullanıcılar uygulamaya 
              giriş yaptığında otomatik olarak OneSignal ID'lerini atar ve kaydeder.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OneSignalMigrationPanel; 